import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AIAssistant from './AIAssistant';
import CreateAdmin from './CreateAdmin';
import LiveMap from './LiveMap';
import Settings from './Settings';

const API = 'https://weridesudan.com/api/admin';

function Login({ onLogin }: { onLogin: (token: string) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleLogin = async () => {
    try {
      const res = await axios.post(`${API}/login`, { email, password });
      onLogin(res.data.token);
    } catch {
      setError('Invalid credentials');
    }
  };

  return (
    <div style={styles.loginContainer}>
      <div style={styles.loginBox}>
        <h1 style={styles.logo}>🚗 We Ride Sudan</h1>
        <h2 style={styles.loginTitle}>Admin Dashboard</h2>
        <input style={styles.input} placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} />
        <input style={styles.input} placeholder="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} />
        {error && <p style={styles.error}>{error}</p>}
        <button style={styles.button} onClick={handleLogin}>Login</button>
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }: { token: string; onLogout: () => void }) {
  const [tab, setTab] = useState('rides');
  const [rides, setRides] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/rides`, { headers }).then(r => setRides(r.data.rides || []));
    axios.get(`${API}/users`, { headers }).then(r => setUsers(r.data.users || []));
    axios.get(`${API}/stats`, { headers }).then(r => setStats(r.data));
  }, [token]);

  const drivers = users.filter(u => u.role === 'driver');
  const riders = users.filter(u => u.role === 'rider');

  const starRating = (val: number | null | undefined) =>
    val != null ? `⭐ ${Number(val).toFixed(1)}` : '—';

  const toggleUser = async (id: string, isActive: boolean) => {
    await axios.patch(`${API}/users/${id}`, { isActive: !isActive }, { headers });
    const res = await axios.get(`${API}/users`, { headers });
    setUsers(res.data.users || []);
  };

  const tabs = ['rides', 'drivers', 'riders', 'stats', 'ai', 'live-map', 'settings', 'create-admin'];

  const tabLabel: Record<string, string> = {
    rides: '🚗 Rides',
    drivers: '🧑 Drivers',
    riders: '👤 Riders',
    stats: '📊 Statistics',
    ai: '🤖 AI Assistant',
    'live-map': '🗺 Live Map',
    settings: '⚙️ Settings',
    'create-admin': '➕ Create Admin',
  };

  return (
    <div style={styles.container}>
      {/* Sidebar */}
      <div style={styles.sidebar}>
        <h2 style={styles.sidebarTitle}>🚗 WeRide Admin</h2>
        {tabs.map(t => (
          <div
            key={t}
            style={{ ...styles.navItem, ...(tab === t ? styles.navActive : {}) }}
            onClick={() => setTab(t)}
          >
            {tabLabel[t]}
          </div>
        ))}
        <div style={{ marginTop: 'auto' }}>
          <div style={styles.navItem} onClick={onLogout}>🚪 Logout</div>
        </div>
      </div>

      {/* Main content */}
      <div style={styles.main}>

        {/* STATS */}
        {tab === 'stats' && stats && (
          <div>
            <h2>📊 Overview</h2>
            <div style={styles.statsGrid}>
              {[
                ['Total Rides', stats.totalRides],
                ['Active Rides', stats.activeRides],
                ['Total Drivers', stats.totalDrivers],
                ['Total Riders', stats.totalRiders],
                ['Total Revenue', `${stats.totalRevenue} SDG`],
              ].map(([label, value]) => (
                <div key={label} style={styles.statCard}>
                  <div style={{ fontSize: 32, fontWeight: 'bold' }}>{value}</div>
                  <div>{label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* RIDES */}
        {tab === 'rides' && (
          <div>
            <h2>🚗 All Rides</h2>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th>Rider</th><th>Driver</th><th>Pickup</th><th>Dropoff</th>
                  <th>Fare</th><th>Status</th><th>Ratings</th>
                </tr>
              </thead>
              <tbody>
                {rides.map(r => (
                  <tr key={r._id}>
                    <td>{r.rider?.name || 'N/A'}</td>
                    <td>{r.driver?.name || 'Unassigned'}</td>
                    <td>{r.pickup?.address || '–'}</td>
                    <td>{r.destination?.address || '–'}</td>
                    <td>{r.fare?.estimated} SDG</td>
                    <td>
                      <span style={{ ...styles.badge, backgroundColor: r.status === 'completed' ? '#4caf50' : r.status === 'cancelled' ? '#f44336' : '#ff9800' }}>
                        {r.status}
                      </span>
                    </td>
                    <td style={{ fontSize: 13, lineHeight: '1.6' }}>
                      {r.status === 'completed' ? (
                        <>
                          <div>🚗 {starRating(r.driverRating ?? r.rating)}</div>
                          <div>👤 {starRating(r.riderRating)}</div>
                        </>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* DRIVERS */}
        {tab === 'drivers' && (
          <div>
            <h2>🧑 Drivers</h2>
            <table style={styles.table}>
              <thead><tr><th>Name</th><th>Phone</th><th>Status</th><th>Rating</th><th>Action</th></tr></thead>
              <tbody>
                {drivers.map(d => (
                  <tr key={d._id}>
                    <td>{d.name}</td>
                    <td>{d.phone}</td>
                    <td>{d.isOnline ? '🟢 Online' : '🔴 Offline'}</td>
                    <td>{starRating(d.rating)}</td>
                    <td>
                      <button
                        style={{ ...styles.actionBtn, backgroundColor: d.isActive ? '#e53935' : '#43a047' }}
                        onClick={() => toggleUser(d._id, d.isActive)}
                      >
                        {d.isActive ? 'Suspend' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* RIDERS */}
        {tab === 'riders' && (
          <div>
            <h2>👤 Riders</h2>
            <table style={styles.table}>
              <thead><tr><th>Name</th><th>Phone</th><th>Email</th><th>Total Rides</th><th>Rating</th><th>Action</th></tr></thead>
              <tbody>
                {riders.map(r => (
                  <tr key={r._id}>
                    <td>{r.name}</td>
                    <td>{r.phone}</td>
                    <td>{r.email}</td>
                    <td>{r.totalRides || 0}</td>
                    <td>{starRating(r.rating)}</td>
                    <td>
                      <button
                        style={{ ...styles.actionBtn, backgroundColor: r.isActive ? '#e53935' : '#43a047' }}
                        onClick={() => toggleUser(r._id, r.isActive)}
                      >
                        {r.isActive ? 'Suspend' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* AI ASSISTANT */}
        {tab === 'ai' && <AIAssistant token={token} />}

        {/* LIVE MAP */}
        {tab === 'live-map' && <LiveMap />}

        {/* SETTINGS */}
        {tab === 'settings' && <Settings token={token} />}

        {/* CREATE ADMIN */}
        {tab === 'create-admin' && <CreateAdmin token={token} />}

      </div>
    </div>
  );
}

export default function App() {
  const [token, setToken] = useState(localStorage.getItem('adminToken') || '');

  const handleLogin = (t: string) => {
    localStorage.setItem('adminToken', t);
    setToken(t);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setToken('');
  };

  return token ? <Dashboard token={token} onLogout={handleLogout} /> : <Login onLogin={handleLogin} />;
}

const styles: any = {
  loginContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#1a1a2e' },
  loginBox: { backgroundColor: '#fff', padding: 40, borderRadius: 12, width: 380, textAlign: 'center' as const },
  logo: { fontSize: 24, marginBottom: 8 },
  loginTitle: { color: '#333', marginBottom: 24 },
  input: { width: '100%', padding: 12, marginBottom: 12, borderRadius: 8, border: '1px solid #ddd', fontSize: 15, boxSizing: 'border-box' as const },
  button: { width: '100%', padding: 14, backgroundColor: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, fontSize: 16, cursor: 'pointer' },
  error: { color: 'red', marginBottom: 12 },
  container: { display: 'flex', height: '100vh', fontFamily: 'sans-serif' },
  sidebar: { width: 220, backgroundColor: '#1a1a2e', color: '#fff', padding: 20, display: 'flex', flexDirection: 'column' as const },
  sidebarTitle: { fontSize: 16, marginBottom: 24 },
  navItem: { padding: '12px 16px', cursor: 'pointer', borderRadius: 8, marginBottom: 8, textTransform: 'capitalize' as const },
  navActive: { backgroundColor: '#1a73e8' },
  main: { flex: 1, padding: 30, backgroundColor: '#f5f5f5', overflowY: 'auto' as const },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginTop: 20 },
  statCard: { backgroundColor: '#fff', padding: 24, borderRadius: 12, textAlign: 'center' as const, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  table: { width: '100%', borderCollapse: 'collapse' as const, backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden' },
  badge: { padding: '4px 10px', borderRadius: 20, color: '#fff', fontSize: 12 },
  actionBtn: { padding: '6px 14px', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer' },
};